import * as v from 'valibot'
import { cronExpressionSchema } from '../scheduling'

/** Document formats a monitored source is expected to publish. */
export const SOURCE_FORMATS = [
  'pdf',
  'html',
  'xlsx',
  'xls',
  'csv',
  'txt',
] as const

/** Monthly on the 1st at 08:00 UTC. */
export const DEFAULT_SCHEDULE = '0 8 1 * *'

export const slackWebhookUrlSchema = v.pipe(
  v.string(),
  v.url('slackWebhookUrl must be a valid URL'),
  v.regex(
    /^https:\/\/hooks\.slack(?:-gov)?\.com\//,
    'slackWebhookUrl must be an https://hooks.slack.com or https://hooks.slack-gov.com URL',
  ),
)

/**
 * A link-matching rule `discover.ts` applies to a landing page's anchors.
 * Serializable (a `regex` value is a pattern string, compiled at match time) so
 * it can be persisted in config and round-tripped through JSON.
 */
export const MatchRuleSchema = v.pipe(
  v.object({
    kind: v.picklist(['regex', 'substring']),
    /**
     * Matched against a link's own text, its URL-decoded href, and the
     * nearest `h1`-`h6` heading. Optional when `row`/`column` alone already
     * pin the link to a table cell — e.g. a fee-schedule table where every
     * cell in a column is just a changing date (WFL-7633).
     */
    value: v.optional(v.pipe(v.string(), v.minLength(1))),
    /**
     * Restrict to a link inside a table row whose header — the row's
     * leading `<th>` — contains this text (case-insensitive substring),
     * e.g. the service line a fee-schedule table names in its left column
     * (WFL-7633).
     */
    row: v.optional(v.pipe(v.string(), v.minLength(1))),
    /**
     * Restrict to a link inside a table column whose header — from the
     * table's leading all-`<th>` row — contains this text (case-insensitive
     * substring), e.g. "Current" vs. an archive column (WFL-7633).
     */
    column: v.optional(v.pipe(v.string(), v.minLength(1))),
    /**
     * Restrict to a link whose nearest `h1`-`h6` heading (table-boundary
     * independent, same field `value` already falls back to) contains this
     * text. Unlike `row`/`column`, which pin a real table cell, this pins a
     * link to a page/section heading — e.g. a link named identically to
     * others on the page, sitting under a distinct heading with no table
     * involved at all (WFL-7656).
     */
    heading: v.optional(v.pipe(v.string(), v.minLength(1))),
    /**
     * How to settle several matching links: `only` (default) requires the rule
     * to isolate exactly one, rejecting as ambiguous otherwise; `first` takes
     * the first match in page order instead — for a page that legitimately
     * stacks a new matching link at the top of a list/table on every publish,
     * where the label alone can never disambiguate (WFL-7629).
     */
    pick: v.optional(v.picklist(['only', 'first'])),
  }),
  v.check(
    (rule) =>
      rule.value !== undefined ||
      rule.row !== undefined ||
      rule.column !== undefined ||
      rule.heading !== undefined,
    'a match rule needs at least one of value, row, column, or heading',
  ),
)

export type MatchRule = v.InferOutput<typeof MatchRuleSchema>

/**
 * Longest navigation a plan may describe. Real instructions run three to six
 * steps; the cap stops a provisioning walk from wandering a site forever.
 */
export const MAX_PLAN_STEPS = 8

const cssSelectorSchema = v.pipe(v.string(), v.minLength(1))

/**
 * One step of a resolved navigation plan.
 *
 * `link` matches the current page's anchors and follows the single match — the
 * only step a plain fetch can perform, so a link-only plan needs no browser.
 * `shadow` marks a link step recorded against a rendered shadow root (TOOL-1147):
 * the anchor does not exist in the fetched HTML, so the step needs the browser
 * replay path like a form interaction does.
 * `fill` / `select` / `click` drive a form and are replayed in a browser
 * session. Their `selector` is computed from the DOM at provisioning time
 * (`#id`, then `[name=…]`, then a positional fallback), never authored by the
 * model, so replay doesn't depend on a model's idea of a good CSS selector.
 * A `click` with `row` is re-targeted on every replay to the one clickable
 * control whose table row contains that text, because grid ids embed the row
 * index (`…Datalist_ctl19_Select`) and shift when rows are added (TOOL-1265).
 * `opensDocument` marks a final click whose response opens the tracked
 * document via `window.open` rather than linking to it.
 */
export const NavigationStepSchema = v.variant('kind', [
  v.object({
    kind: v.literal('link'),
    rule: MatchRuleSchema,
    /** The matched anchor lives in an open shadow root — needs browser replay. */
    shadow: v.optional(v.boolean()),
  }),
  v.object({
    kind: v.literal('fill'),
    selector: cssSelectorSchema,
    value: v.string(),
  }),
  v.object({
    kind: v.literal('select'),
    selector: cssSelectorSchema,
    /** Option value for a `<select>`; omitted for a radio or checkbox. */
    value: v.optional(v.string()),
  }),
  v.object({
    kind: v.literal('click'),
    selector: cssSelectorSchema,
    row: v.optional(v.pipe(v.string(), v.minLength(1))),
    opensDocument: v.optional(v.literal(true)),
  }),
])

export type NavigationStep = v.InferOutput<typeof NavigationStepSchema>
/** A diagnostic prefix of a navigation plan; it need not reach a document. */
export const PartialNavigationPlanSchema = v.pipe(
  v.array(NavigationStepSchema),
  v.minLength(1, 'a partial navigation plan needs at least one step'),
  v.maxLength(MAX_PLAN_STEPS),
)

export type PartialNavigationPlan = v.InferOutput<
  typeof PartialNavigationPlanSchema
>

/**
 * How to get from `startUrl` to the tracked document, resolved once at
 * provisioning and replayed verbatim on every check. Ends in a `link` step, or
 * a `click` that opens the document (TOOL-1265): either way discovery yields
 * a URL, and the document itself is always fetched by the normal
 * conditional-GET path rather than through a browser.
 */
export const NavigationPlanSchema = v.pipe(
  v.array(NavigationStepSchema),
  v.minLength(1, 'a navigation plan needs at least one step'),
  v.maxLength(MAX_PLAN_STEPS),
  v.check((plan) => {
    const last = plan.at(-1)
    return (
      last?.kind === 'link' || (last?.kind === 'click' && !!last.opensDocument)
    )
  }, 'a navigation plan must end with a link step or a click that opens the document'),
  v.check(
    (plan) =>
      plan
        .slice(0, -1)
        .every((step) => step.kind !== 'click' || !step.opensDocument),
    'only the last step of a navigation plan can open the document',
  ),
)

export type NavigationPlan = v.InferOutput<typeof NavigationPlanSchema>

/** Whether replaying a plan requires a browser session rather than plain fetches. */
export const planNeedsBrowser = (plan: NavigationPlan): boolean =>
  plan.some((step) => step.kind !== 'link' || step.shadow)

/**
 * A column with no data in the fetched file, appended during the per-source
 * edit step (WFL-7602) with a value derived from the release's metadata
 * rather than the file's own content.
 */
export const ComputedColumnSchema = v.object({
  name: v.pipe(v.string(), v.minLength(1)),
  /**
   * `fileOrigin` — the downloaded filename. `effectiveStartDate` /
   * `effectiveEndDate` — CCYY-MM-DD, parsed from the filename's release date
   * and its containing calendar quarter (Q1 Jan 1, Q2 Apr 1, Q3 Jul 1, Q4 Oct 1).
   */
  source: v.picklist(['fileOrigin', 'effectiveStartDate', 'effectiveEndDate']),
})

export type ComputedColumn = v.InferOutput<typeof ComputedColumnSchema>

/**
 * A per-source edit applied to freshly fetched bytes before
 * comparison/persistence (WFL-7602): row/column structural changes, columns
 * computed from metadata outside the file itself, rendering an html source
 * to PDF via a real browser print (WFL-7616), or removing html chrome the
 * site's own markup doesn't mark up semantically (WFL-7640) — mutually
 * exclusive with the row/column fields, which only apply to tabular
 * formats. Resolved once at provisioning from `editInstructions` and
 * replayed verbatim on every check — the live-check hot path never calls an
 * LLM for the row/column case, and `renderAsPdf`/`dropSelectors` need no
 * resolution at all (nothing ambiguous to translate).
 *
 * Distinct from — and applied before — the format normalizers in
 * `normalize.ts`: an edit rule changes what's actually persisted (the R2
 * artifact, both candidate and baseline), while normalization is a
 * comparison-only step that is never persisted and, unlike an edit rule, is
 * applied symmetrically to whichever bytes are being compared (the fresh
 * candidate *and* the stored baseline).
 */

export const EditRuleSchema = v.object({
  /** 1-indexed rows of the raw sheet (before any drop) to remove outright. */
  dropRows: v.optional(
    v.array(v.pipe(v.number(), v.integer(), v.minValue(1))),
    [],
  ),
  /** Header renames: the fetched column name -> the canonical name. */
  columnRenames: v.optional(
    v.record(v.string(), v.pipe(v.string(), v.minLength(1))),
    {},
  ),
  /** Columns appended after renaming, in order. */
  addColumns: v.optional(v.array(ComputedColumnSchema), []),
  /**
   * Print the fetched html page to PDF via a real browser session before
   * persisting/comparing it (WFL-7616) — reproduces a "print to PDF"
   * curation a human once did by hand for sources whose approved baseline
   * is a PDF render, not raw HTML. `format` becomes `pdf` from that point
   * on: r2Key, `normalizeContent`, and `CONTENT_TYPES` all follow the
   * rendered bytes, not the source's original html.
   */
  renderAsPdf: v.optional(v.boolean(), false),
  /**
   * CSS selectors for html-page chrome the site's own markup doesn't mark
   * up semantically (WFL-7640) — e.g. a content-authored quick-jump/anchor
   * nav block that's a plain `<div>`, not a `<nav>`/`role="navigation"`, so
   * the global `BOILERPLATE_SELECTOR` never matches it. Applied wherever
   * `BOILERPLATE_SELECTOR` is: the static-html stored-artifact path
   * (`stripHtmlBoilerplate`) and the live browser-print path just before a
   * `renderAsPdf` capture. Like `renderAsPdf`, this is JSON-shortcut only,
   * never LLM-resolved — a literal selector list has nothing ambiguous to
   * translate, and only a human who inspected the page's real DOM can
   * supply one.
   */
  dropSelectors: v.optional(
    v.array(
      v.pipe(
        v.string(),
        v.trim(),
        v.minLength(
          1,
          'dropSelectors entries cannot be empty or whitespace-only',
        ),
        // Handed to HTMLRewriter's own parser — the exact engine
        // `dropSelectors` is later joined into and passed to
        // (`stripHtmlBoilerplate`'s `HTMLRewriter.on`, and the equivalent
        // `document.querySelectorAll` on the browser-print path), which
        // throws synchronously on a malformed selector. Reusing it here
        // catches an operator's typo at config-parse time instead of deep
        // inside a live check.
        v.check((selector) => {
          try {
            new HTMLRewriter().on(selector, {})
            return true
          } catch {
            return false
          }
        }, 'dropSelectors entries must be valid CSS selectors'),
      ),
    ),
    [],
  ),
  /**
   * CSS selectors for collapsed regions to force-reveal before a
   * `renderAsPdf` capture (e.g. a CMS accordion whose content div is
   * `visibility:hidden` + `grid-template-rows:0fr` until its own JS toggles
   * a class — no print stylesheet expands it, so a plain print captures
   * only the collapsed headers). Reveal is a style override injected into
   * the live page, never a click: clicking depends on the site's JS and
   * cannot round-trip through the `setContent` WAF fallback. Print-path
   * only — the static `stripHtmlBoilerplate` path has no CSS to defeat.
   * JSON-shortcut only, like `dropSelectors`, except it is NOT
   * HTMLRewriter-validated: selectors here only ever reach
   * `document.querySelectorAll`, so browser-only syntax (`:has()`) is legal.
   */
  expandSelectors: v.optional(
    v.array(
      v.pipe(
        v.string(),
        v.trim(),
        v.minLength(
          1,
          'expandSelectors entries cannot be empty or whitespace-only',
        ),
      ),
    ),
    [],
  ),
  /**
   * Extract the page's own data table into CSV before persisting/comparing
   * it (WFL-7703) — some sources publish their document as an HTML table
   * rather than a downloadable file, and reviewing/diffing a table is far
   * easier as CSV than as raw markup. `format` becomes `csv` from that
   * point on, exactly like `renderAsPdf` becomes `pdf`: r2Key,
   * `normalizeContent`, and `CONTENT_TYPES` all follow the extracted CSV.
   * Mutually exclusive with `renderAsPdf` (rejected in `check.ts`) — a page
   * can't become both a PDF render and a table CSV. JSON-shortcut only,
   * like `renderAsPdf`/`dropSelectors`: a boolean has nothing ambiguous for
   * a model to translate.
   */
  scrapeTable: v.optional(v.boolean(), false),
})

export type EditRule = v.InferOutput<typeof EditRuleSchema>

/**
 * Report monitor: an LLM-driven analyst that, each run, fetches policy updates,
 * cross-references them against Rialtic policies, stores recommendations, and
 * posts a Slack summary.
 */
export const ReportMonitorConfigSchema = v.object({
  type: v.literal('report'),
  /** Natural language description of what this agent is monitoring and why. */
  goal: v.pipe(v.string(), v.minLength(1, 'goal is required')),
  /** Cron expression (e.g. `"0 8 * * 1"` = Mondays at 08:00 UTC). */
  schedule: cronExpressionSchema,
  /** Step-by-step instructions the LLM follows during each run. */
  instructions: v.pipe(v.string(), v.minLength(1, 'instructions is required')),
  /** Slack Incoming Webhook URL for run summaries. */
  slackWebhookUrl: slackWebhookUrlSchema,
  /** Slack channel ID (e.g. C01234567) to share the CSV file into. */
  slackChannelId: v.optional(v.string()),
})

/**
 * Source monitor: a deterministic change detector that tracks one document
 * stream, stages candidate replacements when the normalized content changes,
 * and notifies Slack on candidates and operational alerts.
 */
export const SourceMonitorConfigSchema = v.pipe(
  v.object({
    type: v.literal('source'),
    /** Human-readable name of the tracked source document stream. */
    sourceName: v.pipe(v.string(), v.minLength(1, 'sourceName is required')),
    /** Page (or direct document URL) each check starts from. */
    startUrl: v.pipe(v.string(), v.url('startUrl must be a valid URL')),
    expectedFormat: v.picklist(SOURCE_FORMATS),
    /**
     * How the document is reached: `direct` — `startUrl` is the document, fetch it
     * directly; `discover` — `startUrl` is a landing page, the document link is
     * discovered from it before fetching.
     */
    directive: v.optional(v.picklist(['direct', 'discover'])),
    /** Cron expression (5 fields); defaults to monthly. */
    schedule: v.optional(cronExpressionSchema, DEFAULT_SCHEDULE),
    /**
     * Days after the scheduled check during which a missing or unchanged
     * document is still expected rather than alert-worthy (publication lag).
     */
    bufferWindowDays: v.optional(
      v.pipe(
        v.number(),
        v.integer('bufferWindowDays must be an integer'),
        v.minValue(0, 'bufferWindowDays must be >= 0'),
      ),
      0,
    ),
    /**
     * How to locate the document when the start URL is a landing page, in plain
     * human language (or a legacy Playwright locator). Never consumed directly by
     * a live check — `provision()` resolves it into `resolvedPlan` once.
     */
    discoveryInstructions: v.optional(v.string()),
    /**
     * The navigation `discoveryInstructions` resolved to, derived at provision
     * time (deterministic parse first, then a browser-driven walk for plain
     * language). Stored separately so the human's wording is never overwritten
     * and re-provisioning unchanged instructions needs no new inference.
     */
    resolvedPlan: v.optional(NavigationPlanSchema),
    /**
     * Partial plan from the most recent failed provisioning walk. Diagnostic only:
     * it is never replayed by a live check.
     */
    lastDiscoveryPlanAttempt: v.optional(PartialNavigationPlanSchema),
    /** Why the most recent discovery-plan attempt did not resolve. */
    lastDiscoveryPlanReason: v.optional(v.string()),
    /**
     * How to edit the fetched bytes before comparison/persistence, in plain
     * human language — curation steps a content team has been performing by
     * hand (WFL-7602). Never consumed directly by a live check —
     * `provision()` resolves it into `resolvedEdit` once. Distinct from the
     * comparison-only normalization every format goes through: an edit
     * changes what's persisted; normalization never is, and applies to the
     * baseline as much as to a fresh candidate.
     */
    editInstructions: v.optional(v.string()),
    /**
     * The edit rule `editInstructions` resolved to, derived at provision time
     * via a one-shot LLM call. Stored separately so the human's wording is
     * never overwritten and re-provisioning unchanged instructions needs no
     * new inference. Applied only to a freshly fetched candidate — never
     * replayed against an already-persisted baseline.
     */
    resolvedEdit: v.optional(EditRuleSchema),
    /** Rialtic reference document this source backs, if already linked. */
    referenceDocumentId: v.optional(v.pipe(v.string(), v.minLength(1))),
    /** Slack Incoming Webhook URL for candidate and alert notifications. */
    slackWebhookUrl: v.optional(slackWebhookUrlSchema),
    /** Slack channel ID (e.g. C01234567) to post into and thread the diff under. */
    slackChannelId: v.optional(v.string()),
  }),
  /* `directive` is optional on the wire so a seed can omit it; every reader
     expects a value, so default it here rather than at each call site. */
  v.transform(({ directive, ...rest }) => ({
    ...rest,
    directive: directive ?? 'direct',
  })),
)

export const MonitorConfigSchema = v.variant('type', [
  ReportMonitorConfigSchema,
  SourceMonitorConfigSchema,
])

export type MonitorConfig = v.InferOutput<typeof MonitorConfigSchema>
export type ReportMonitorConfig = v.InferOutput<
  typeof ReportMonitorConfigSchema
>
export type SourceMonitorConfig = v.InferOutput<
  typeof SourceMonitorConfigSchema
>
export type MonitorType = MonitorConfig['type']

// ---------------------------------------------------------------------------
// Report monitor domain types
// ---------------------------------------------------------------------------

export interface Recommendation {
  id: string
  document_display_id: string
  /** CMS.gov's internal id for the source document — pairs with document_version to build a link to the article. */
  document_id?: string
  /** CMS.gov's version number for the source document at the time it was fetched. */
  document_version?: number
  /** CMS.gov's document type code (e.g. "NCD", "NCA", "Article", "LCA") — determines which CMS.gov URL pattern to build. */
  document_type?: string
  title: string
  detail: string
  severity: 'info' | 'warning' | 'action_required'
  createdAt: string
  effective_date?: string
  updated_on?: string
  retirement_date?: string
  /** ISO timestamp set when a content team member marks this applied; null while pending. */
  appliedAt: string | null
}

// ---------------------------------------------------------------------------
// Source monitor domain types
// ---------------------------------------------------------------------------

export const RUN_OUTCOMES = [
  'no-actionable-change',
  'candidate-staged',
  'error',
] as const

export type RunOutcome = (typeof RUN_OUTCOMES)[number]

/**
 * How a check's outcome was determined. Raw bytes can change while the
 * normalized content is identical, so each level is recorded distinctly:
 * - `http-304` — server reported not modified (ETag/Last-Modified)
 * - `raw-hash-match` — pre-edit fetched bytes hash equal to the approved
 *   version's, under the same edit rule (or lack of one)
 * - `edited-hash-match` — raw bytes (or the edit rule) differed, but the
 *   edited candidate's bytes are byte-for-byte identical to the baseline's
 *   own stored bytes — cheaper than `normalized-hash-match` since neither
 *   side needed normalizing
 * - `normalized-hash-match` — edited bytes differed but normalized content matched
 * - `normalized-hash-mismatch` — normalized content differed; a candidate was staged
 */
export const RUN_DETERMINATIONS = [
  'http-304',
  'raw-hash-match',
  'edited-hash-match',
  'normalized-hash-match',
  'normalized-hash-mismatch',
] as const

export type RunDetermination = (typeof RUN_DETERMINATIONS)[number]

export interface MonitorRun {
  id: string
  checkedAt: string
  outcome: RunOutcome
  /** Null when the check errored before any comparison was made. */
  determination: RunDetermination | null
  /** Hash the fetched content was compared against; null when none applied. */
  comparedHash: string | null
  /** Candidate staged by this run, when outcome is `candidate-staged`. */
  candidateId?: string
  error?: string
}

export interface ApprovedVersion {
  r2Key: string
  /**
   * Hash of the normalized content as of the last time it was computed —
   * informational (the run history's `comparedHash`), not authoritative:
   * a live check always re-derives this baseline's own normalized content
   * fresh from `r2Key` rather than trusting this field, so a normalizer
   * change (or an edit rule that only now applies) can never wedge a
   * source on a stale comparison forever.
   */
  contentHash: string
  /** Hash of the raw fetched bytes; lets an unchanged fetch skip normalization. */
  rawHash?: string
  fetchedAt: string
  etag?: string
  lastModified?: string
  /**
   * PAAPI `ReferenceDocumentVersion.id` this baseline was seeded from
   * (WFL-7529). Only set when seeded from a record — a later live-detected
   * approval has no PAAPI version to point at.
   */
  referenceDocumentVersionId?: string
  /** PAAPI `SourceDocument.id` backing a record-seeded baseline. */
  sourceDocumentId?: string
  /**
   * Fingerprint of the `resolvedEdit` rule in effect when this version's
   * `rawHash` was computed (WFL-7602). `undefined` when no rule applied.
   * Changing/adding the rule is a config-only edit that never touches the
   * source's raw bytes, so without this the raw-hash short-circuit would
   * keep matching a stale approval forever — the new rule's output would
   * never be computed or compared.
   */
  editRuleHash?: string
}

export const CANDIDATE_STATUSES = ['new', 'approved', 'dismissed'] as const

export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number]

export interface CandidateVersion {
  id: string
  r2Key: string
  /** Hash of the normalized content; candidates dedupe on this and `editRuleHash` together (WFL-7602). */
  contentHash: string
  /** Hash of the raw fetched bytes, carried onto the approved version. */
  rawHash?: string
  /**
   * Fingerprint of the `resolvedEdit` rule that produced this content.
   * Carried onto the approved version so a later rule change is detected
   * even against byte-identical content, and candidates dedupe on this
   * alongside `contentHash` (WFL-7602).
   */
  editRuleHash?: string
  /** Lifecycle state: new, approved, or dismissed. */
  status: CandidateStatus
  detectedAt: string
  /** URL the document was actually fetched from (may differ from startUrl). */
  sourceUrl?: string
  etag?: string
  lastModified?: string
}

export const ALERT_TYPES = [
  'source-missing',
  'source-ambiguous',
  'fetch-error',
  'no-extractable-text',
  'discovery-unresolved',
] as const

export type AlertType = (typeof ALERT_TYPES)[number]

export interface MonitorAlert {
  id: string
  type: AlertType
  message: string
  createdAt: string
}

// ---------------------------------------------------------------------------
// Unified state
// ---------------------------------------------------------------------------

/**
 * Flat union of both monitor modes. Report fields and source fields coexist at
 * the top level; a given instance only populates the fields for its configured
 * `type`. Config itself is stored server-side via Think's `configure()`, not in
 * state, so it is never broadcast to connected clients.
 */
export interface MonitorState {
  /** Mirrors the configured monitor type; null before `/configure`. */
  type: MonitorType | null
  // --- report ---
  recommendations: Recommendation[]
  /** Tracks items already reported, keyed by document_display_id, to prevent duplicates. */
  reportedItems: Record<string, { reportedAt: string }>
  lastRunAt: string | null
  lastRunStatus: 'success' | 'error' | null
  // --- source ---
  /** Audit trail of every check, newest first — including no-change checks. */
  runs: MonitorRun[]
  /** Last accepted version of the tracked document. */
  approvedVersion: ApprovedVersion | null
  /** Newly detected replacements awaiting review, newest first. */
  candidates: CandidateVersion[]
  /** Operational issues, newest first. */
  alerts: MonitorAlert[]
  lastCheckedAt: string | null
  lastOutcome: RunOutcome | null
}

export const INITIAL_STATE: MonitorState = {
  type: null,
  recommendations: [],
  reportedItems: {},
  lastRunAt: null,
  lastRunStatus: null,
  runs: [],
  approvedVersion: null,
  candidates: [],
  alerts: [],
  lastCheckedAt: null,
  lastOutcome: null,
}
